import { Check, ChevronRight, SkipForward, X } from "lucide-react";
import React, { useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useApp } from "@/contexts/AppContext";
import type {
  OnboardingStepId,
  StepStatus,
} from "@/contexts/OnboardingContext";
import {
  COMPLETION_STEP,
  ONBOARDING_STEPS,
  useOnboarding,
  WELCOME_STEP,
} from "@/contexts/OnboardingContext";
import { getForecastTerm } from "@/lib/terminology-config";
import { cn } from "@/lib/utils";
import { CompletionScreen } from "./CompletionScreen";
import { StepDashboardLayout } from "./StepDashboardLayout";
import { StepDataSources } from "./StepDataSources";
import { StepGeneralSettings } from "./StepGeneralSettings";
import { StepRolesPermissions } from "./StepRolesPermissions";
import { StepUserManagement } from "./StepUserManagement";
import { WelcomeScreen } from "./WelcomeScreen";

// ── Sidebar step indicator ────────────────────────────────────────────────────

interface StepIndicatorProps {
  stepNumber: number;
  name: string;
  description: string;
  /** Persisted status of the step. */
  status: StepStatus;
  /** Whether this step is the one currently being viewed. */
  isViewing: boolean;
  onSelect: () => void;
}

const STATUS_LABELS: Record<StepStatus, string> = {
  NotStarted: "Not started",
  Started: "In progress",
  Skipped: "Skipped",
  Completed: "Completed",
};

const StepIndicator: React.FC<StepIndicatorProps> = ({
  stepNumber,
  name,
  description,
  status,
  isViewing,
  onSelect,
}) => (
  <button
    type="button"
    onClick={onSelect}
    aria-current={isViewing ? "step" : undefined}
    aria-label={`Step ${stepNumber}: ${name} — ${STATUS_LABELS[status]}`}
    className={cn(
      "w-full text-left flex items-start gap-3 py-2 px-2 rounded-md transition-colors",
      "hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary",
      isViewing && "bg-primary/10",
    )}
  >
    <div
      className={cn(
        "flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold mt-0.5 transition-all",
        status === "NotStarted" && "bg-muted text-muted-foreground",
        status === "Started" &&
          "bg-muted text-muted-foreground ring-2 ring-primary",
        status === "Skipped" && "bg-muted text-amber-600 ring-2 ring-amber-400",
        status === "Completed" && "bg-primary text-primary-foreground",
      )}
    >
      {status === "Completed" ? (
        <Check className="h-3.5 w-3.5" />
      ) : status === "Skipped" ? (
        <SkipForward className="h-3.5 w-3.5" />
      ) : (
        stepNumber
      )}
    </div>
    <div className="min-w-0">
      <p
        className={cn(
          "text-sm font-medium leading-tight",
          status === "NotStarted" && !isViewing
            ? "text-muted-foreground"
            : "text-foreground",
        )}
      >
        {name}
      </p>
      <p
        className={cn(
          "text-xs leading-tight mt-0.5 truncate",
          status === "Skipped" ? "text-amber-600" : "text-muted-foreground",
        )}
      >
        {status === "NotStarted" ? description : STATUS_LABELS[status]}
      </p>
    </div>
  </button>
);

// ── Main OnboardingAssistant shell ────────────────────────────────────────────

export const OnboardingAssistant: React.FC = () => {
  const { currentUser } = useApp();
  const {
    currentStep,
    dismissOnboarding,
    goToStep,
    prevStep,
    completeCurrentStep,
    skipCurrentStep,
    getStepStatus,
  } = useOnboarding();

  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);

  const isWelcome = currentStep === WELCOME_STEP;
  const isCompletion = currentStep === COMPLETION_STEP;
  const isStep = !isWelcome && !isCompletion;

  const currentStepData = isStep ? ONBOARDING_STEPS[currentStep - 1] : null;
  const isLastStep = currentStep === ONBOARDING_STEPS.length;

  // Steps that Skip Onboarding would record as skipped.
  const outstandingStepNames = ONBOARDING_STEPS.filter(
    (step) => getStepStatus(step.id) !== "Completed",
  ).map((step) => step.name);

  // Type B steps start with an empty state; user can expand to see the real page
  const [expandedSteps, setExpandedSteps] = useState<Set<OnboardingStepId>>(
    new Set(),
  );

  const toggleExpanded = (id: OnboardingStepId) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const firstName = currentUser.name.split(" ")[0];

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return <WelcomeScreen firstName={firstName} />;
      case 1:
        return <StepGeneralSettings />;
      case 2:
        return <StepDataSources />;
      case 3:
        return (
          <StepRolesPermissions
            expanded={expandedSteps.has("roles-permissions")}
            onExpand={() => toggleExpanded("roles-permissions")}
          />
        );
      case 4:
        return (
          <StepUserManagement
            expanded={expandedSteps.has("user-management")}
            onExpand={() => toggleExpanded("user-management")}
          />
        );
      case 5:
        return (
          <StepDashboardLayout
            expanded={expandedSteps.has("dashboard-layout")}
            onExpand={() => toggleExpanded("dashboard-layout")}
          />
        );
      case COMPLETION_STEP:
        return <CompletionScreen />;
      default:
        return null;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex bg-white"
      aria-modal="true"
      role="dialog"
      aria-label="Onboarding Assistant"
    >
      {/* ── LEFT SIDEBAR ──────────────────────────────────────────────────── */}
      <aside className="w-[280px] flex-shrink-0 bg-primary/5 border-r border-border flex flex-col">
        {/* Logo + app name */}
        <div className="px-5 pt-5 pb-4">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center flex-shrink-0">
              <span className="text-primary-foreground text-sm font-bold">
                CF
              </span>
            </div>
            <span className="font-semibold text-sm text-foreground leading-tight">
              Access Cash Forecaster
            </span>
          </div>
          <p className="mt-3 text-[10px] uppercase tracking-widest text-muted-foreground font-medium">
            Onboarding Setup
          </p>
        </div>

        <div className="mx-4 border-t border-border" />

        {/* Step indicators — clickable navigation; navigating never changes a status */}
        <nav
          className="flex-1 px-2 py-3 space-y-1 overflow-y-auto"
          aria-label="Onboarding steps"
        >
          {ONBOARDING_STEPS.map((step) => (
            <StepIndicator
              key={step.id}
              stepNumber={step.number}
              name={step.name}
              description={step.sidebarDescription}
              status={getStepStatus(step.id)}
              isViewing={currentStep === step.number}
              onSelect={() => goToStep(step.number)}
            />
          ))}
        </nav>
      </aside>

      {/* ── MAIN CONTENT AREA ─────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top bar */}
        <div className="h-16 flex-shrink-0 flex items-center justify-between px-6 border-b border-border bg-white">
          <div className="flex items-center gap-3 min-w-0">
            {isStep && currentStepData && (
              <>
                <span className="text-sm text-muted-foreground">
                  Step {currentStep} of {ONBOARDING_STEPS.length}
                </span>
                <span className="text-muted-foreground/40">·</span>
                <span className="text-sm font-medium text-foreground truncate">
                  {currentStepData.name}
                </span>
              </>
            )}
            {isWelcome && (
              <span className="text-sm font-medium text-foreground">
                Welcome to Access Cash Forecaster
              </span>
            )}
            {isCompletion && (
              <span className="text-sm font-medium text-foreground">
                Setup Summary
              </span>
            )}
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="text-sm text-muted-foreground hover:text-foreground flex-shrink-0"
            onClick={() => setSkipConfirmOpen(true)}
          >
            <X className="h-4 w-4 mr-1.5" />
            Skip Onboarding
          </Button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto min-h-0">
          {renderStepContent()}
        </div>

        {/* Bottom action bar — steps only, not Welcome or Summary */}
        {isStep && currentStepData && (
          <div className="flex-shrink-0 border-t border-border bg-white px-6 py-4 flex items-center justify-between gap-4">
            <NudgeMessage stepId={currentStepData.id} />

            <div className="flex items-center gap-2 flex-shrink-0">
              {currentStep > 1 && (
                <Button variant="outline" size="sm" onClick={prevStep}>
                  ← Back
                </Button>
              )}

              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={() => goToStep(COMPLETION_STEP)}
              >
                Review Setup
              </Button>

              <Button
                variant="ghost"
                size="sm"
                className="text-muted-foreground"
                onClick={skipCurrentStep}
              >
                Skip for now
              </Button>

              <Button size="sm" onClick={completeCurrentStep}>
                {isLastStep ? (
                  <>
                    Finish Setup <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                ) : (
                  <>
                    Mark as done <ChevronRight className="h-4 w-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Skip Onboarding confirmation — force-completes the series */}
      <AlertDialog open={skipConfirmOpen} onOpenChange={setSkipConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Skip Onboarding?</AlertDialogTitle>
            <AlertDialogDescription>
              {outstandingStepNames.length > 0 ? (
                <>
                  Onboarding will be marked complete and these steps will be
                  recorded as skipped: {outstandingStepNames.join(", ")}. You
                  can configure them from the Admin screens, or restart
                  onboarding from the sidebar at any time.
                </>
              ) : (
                <>
                  Every step is already complete. Onboarding will be marked
                  complete and closed.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={dismissOnboarding}>
              Skip Onboarding
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ── Nudge messages per step ───────────────────────────────────────────────────

const NudgeMessage: React.FC<{ stepId: OnboardingStepId }> = ({ stepId }) => {
  const forecastTerm = getForecastTerm(false); // e.g. "forecast" / "projection" / "outlook"

  const messages: Record<OnboardingStepId, string> = {
    "general-settings": `Set your financial year, currency and ${forecastTerm} defaults before your team starts. You can update these from Settings at any time.`,
    "data-sources": `Connecting your accounting, payroll and banking systems is what powers the ${forecastTerm}. The more you connect, the more accurate your cash position.`,
    "roles-permissions":
      "Cash Forecaster includes three default roles. Configure custom roles here before assigning users — or skip this and assign users to the default roles. You can customise roles at any time from Admin → Roles & Permissions.",
    "user-management": `Add your finance team so they can access their ${forecastTerm}s and reports. You can do this now or come back to it later from Settings.`,
    "dashboard-layout":
      "Configure which cards appear on the dashboard for each role. This helps each user see the most relevant information immediately.",
  };

  return (
    <p className="text-xs text-muted-foreground max-w-lg leading-relaxed">
      {messages[stepId]}
    </p>
  );
};
